import { type ChangeEvent, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload } from "lucide-react";
import { apiPost } from "@/lib/apiClient";
import { withKbMembership } from "@/lib/knowledgeBaseRegistry";
import { articlePath, type KnowledgeArticle } from "@/lib/knowledgeModel";
import { isMarkdownFilename, readMarkdownFile } from "@/lib/knowledgeMarkdownUpload";
import { knowledgeSecondaryLinkClass } from "@/pages/knowledge/KnowledgeChrome";
import { useKnowledgeBases } from "@/pages/knowledge/useKnowledgeBases";

/**
 * Pick a .md file, create an article via POST /api/knowledge/articles, then open the view.
 * Books stay optional. The active knowledge base membership key is applied when needed.
 * Status defaults to draft.
 */
export function UploadMarkdownButton({
  onError,
}: {
  onError?: (message: string) => void;
}) {
  const navigate = useNavigate();
  const { activeId } = useKnowledgeBases();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function onFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!isMarkdownFilename(file.name)) {
      onError?.("Only .md (markdown) files are accepted.");
      return;
    }
    if (!activeId.trim()) {
      onError?.("Select or create a knowledge base first.");
      return;
    }

    setBusy(true);
    try {
      const { title, markdown } = await readMarkdownFile(file);
      const res = await apiPost<KnowledgeArticle>("/api/knowledge/articles", {
        title,
        markdown,
        status: "draft",
        collectionKeys: withKbMembership([], activeId),
      });
      if (!res.ok || !res.data?.id) {
        onError?.(res.error || "Could not create article from markdown file.");
        return;
      }
      void navigate(articlePath(activeId, res.data.id));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not read the markdown file.";
      onError?.(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        disabled={busy}
        className={knowledgeSecondaryLinkClass}
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="h-4 w-4" aria-hidden />
        {busy ? "Uploading…" : "Upload markdown (.md)"}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".md,text/markdown"
        className="sr-only"
        aria-label="Upload markdown file"
        disabled={busy}
        onChange={(event) => void onFileChange(event)}
      />
    </>
  );
}
